import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { 
  FiArrowLeft, FiEdit, FiTrash2, FiUser, FiMail, FiPhone, FiCalendar, FiShield,
  FiDollarSign, FiActivity, FiClock, FiToggleLeft, FiToggleRight
} from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'
import FormInput from '../../components/FormInput/FormInput'
import Select from '../../components/Select/Select'
import Table from '../../components/Table/Table'
import './UserDetail.css'

const ROLE_OPTIONS = [
  { value: 'student', label: 'Student' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'batch manager', label: 'Batch Manager' },
  { value: 'doubt solver', label: 'Doubt Solver' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Super Admin' },
]

const UserDetail = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const { user: currentUser } = useSelector((state) => state.auth)
  
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [payments, setPayments] = useState([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview') // overview, payments, activity
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
  })

  useEffect(() => {
    loadUser()
  }, [userId])

  useEffect(() => {
    if (user?.role === 'student' && user?._id) {
      loadPayments()
    }
  }, [user?._id, user?.role])

  const loadUser = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminApi.get(`/users/${userId}`)
      setUser(response.data)
      setFormData({
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        email: response.data.email || '',
        phone: response.data.phone || response.data.mobileNumber || '',
        role: response.data.role || '',
      })
      
      // Load payments if student
      if (response.data.role === 'student') {
        loadPayments()
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }

  const loadPayments = async () => {
    setPaymentsLoading(true)
    try {
      // Try the user-specific endpoint first, fallback to filtering all payments
      try {
        const response = await adminApi.get(`/payments/user/${userId}`)
        setPayments(response.data?.data || response.data || [])
      } catch (err) {
        // Fallback: Get all payments and filter by user
        const response = await adminApi.get(`/payments`)
        const allPayments = response.data?.data || response.data || []
        const userPayments = allPayments.filter(
          payment => payment.user?._id === userId || payment.user === userId
        )
        setPayments(userPayments)
      }
    } catch (err) {
      console.error('Failed to load payments:', err)
      setPayments([])
    } finally {
      setPaymentsLoading(false)
    }
  }

  const handleToggleActive = async () => {
    try {
      const isActive = user?.isActive !== false
      await adminApi.patch(`/users/${userId}`, { 
        isActive: !isActive,
        active: !isActive 
      })
      loadUser()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user status')
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setEditLoading(true)
    try {
      const response = await adminApi.patch(`/users/${userId}/role`, { role: formData.role })
      setUser(response.data)
      setShowEditModal(false)
      loadUser()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user')
    } finally {
      setEditLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      await adminApi.delete(`/users/${userId}`)
      navigate('/users')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user')
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return <div className="detail-loading">Loading user details...</div>
  }

  if (error && !user) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate('/users')}>Back to Users</Button>
      </div>
    )
  }

  return (
    <div className="user-detail-page">
      <header className="detail-header">
        <Link to="/users" className="back-link">
          <FiArrowLeft size={20} />
          Back to Users
        </Link>
        <div className="header-actions">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            <FiEdit size={18} />
            Edit User
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={user?.role === 'superadmin' && currentUser?.role !== 'superadmin'}
          >
            <FiTrash2 size={18} />
            Delete
          </Button>
        </div>
      </header>

      <div className="detail-content">
        <div className="detail-main">
          <section className="detail-card">
            <div className="card-header">
              <div className="user-avatar-large">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div>
                <h1>{user?.firstName} {user?.lastName}</h1>
                <StatusBadge status={user?.emailVerified ? 'active' : 'pending'} />
              </div>
            </div>

            <div className="info-grid">
              <div className="info-item">
                <FiMail size={18} />
                <div>
                  <span className="info-label">Email</span>
                  <strong>{user?.email}</strong>
                </div>
              </div>
              <div className="info-item">
                <FiPhone size={18} />
                <div>
                  <span className="info-label">Phone</span>
                  <strong>{user?.phone || user?.mobileNumber || '—'}</strong>
                </div>
              </div>
              <div className="info-item">
                <FiShield size={18} />
                <div>
                  <span className="info-label">Role</span>
                  <strong>{user?.role || '—'}</strong>
                </div>
              </div>
              <div className="info-item">
                <FiCalendar size={18} />
                <div>
                  <span className="info-label">Joined</span>
                  <strong>
                    {user?.createdAt
                      ? new Date(user?.createdAt).toLocaleDateString()
                      : '—'}
                  </strong>
                </div>
              </div>
              <div className="info-item">
                <div>
                  <span className="info-label">Account Status</span>
                  <div className="status-control">
                    <strong>{user?.isActive !== false ? 'Active' : 'Inactive'}</strong>
                    <button
                      type="button"
                      className="toggle-status-btn"
                      onClick={handleToggleActive}
                      title={user?.isActive !== false ? 'Deactivate' : 'Activate'}
                    >
                      {user?.isActive !== false ? (
                        <FiToggleRight size={24} className="active" />
                      ) : (
                        <FiToggleLeft size={24} className="inactive" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section className="detail-card">
            <div className="tabs">
              <button
                type="button"
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              {user?.role === 'student' && (
                <>
                  <button
                    type="button"
                    className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payments')}
                  >
                    <FiDollarSign size={16} />
                    Payments
                  </button>
                </>
              )}
              <button
                type="button"
                className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                <FiActivity size={16} />
                Activity
              </button>
            </div>
          </section>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {user?.role === 'student' && (
                <section className="detail-card">
                  <h2>Enrollments</h2>
                  <div className="enrollments-list">
                    {user.enrollments?.length > 0 ? (
                      user.enrollments.map((enrollment) => (
                        <div key={enrollment._id} className="enrollment-item">
                          <div>
                            <strong>{enrollment.batch?.batchName || 'Unnamed Batch'}</strong>
                            <span>{enrollment.course?.title || enrollment.batch?.course?.title}</span>
                          </div>
                          <StatusBadge status={enrollment.status} />
                        </div>
                      ))
                    ) : (
                      <p className="empty-state">No enrollments</p>
                    )}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && user?.role === 'student' && (
            <section className="detail-card">
              <h2>Payment History</h2>
              {paymentsLoading ? (
                <div className="loading-state">Loading payments...</div>
              ) : payments.length > 0 ? (
                <Table
                  columns={[
                    {
                      key: 'amount',
                      label: 'Amount',
                      render: (row) => `₹${row.amount || 0}`,
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      render: (row) => <StatusBadge status={row.status || 'pending'} />,
                    },
                    {
                      key: 'method',
                      label: 'Method',
                      render: (row) => row.paymentMethod || '—',
                    },
                    {
                      key: 'date',
                      label: 'Date',
                      render: (row) => 
                        row.createdAt 
                          ? new Date(row.createdAt).toLocaleDateString()
                          : '—',
                    },
                    {
                      key: 'actions',
                      label: 'Actions',
                      render: (row) => (
                        <Link to={`/payments/${row._id}`} className="action-link">
                          View Details
                        </Link>
                      ),
                    },
                  ]}
                  data={payments}
                  emptyMessage="No payments found"
                />
              ) : (
                <p className="empty-state">No payment history</p>
              )}
            </section>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <section className="detail-card">
              <h2>Activity Log</h2>
              <div className="activity-list">
                <div className="activity-item">
                  <FiClock size={16} />
                  <div>
                    <strong>Account Created</strong>
                    <span>
                      {user?.createdAt
                        ? new Date(user.createdAt).toLocaleString()
                        : '—'}
                    </span>
                  </div>
                </div>
                {user?.lastLogin && (
                  <div className="activity-item">
                    <FiClock size={16} />
                    <div>
                      <strong>Last Login</strong>
                      <span>
                        {new Date(user.lastLogin).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
                {user?.updatedAt && (
                  <div className="activity-item">
                    <FiClock size={16} />
                    <div>
                      <strong>Last Updated</strong>
                      <span>
                        {new Date(user.updatedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
                {user?.emailVerifiedAt && (
                  <div className="activity-item">
                    <FiMail size={16} />
                    <div>
                      <strong>Email Verified</strong>
                      <span>
                        {new Date(user.emailVerifiedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              {user?.role === 'student' && (
                <Button variant="secondary" fullWidth>
                  View Enrollments
                </Button>
              )}
              <Button variant="secondary" fullWidth onClick={() => setShowEditModal(true)}>
                Change Role
              </Button>
            </div>
          </section>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit User"
        size="medium"
      >
        <form onSubmit={handleUpdate}>
          <FormInput
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            disabled
          />
          <FormInput
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            disabled
          />
          <FormInput
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            disabled
          />
          <Select
            label="Role"
            name="role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            options={ROLE_OPTIONS}
            required
            disabled={user?.role === 'superadmin' && currentUser?.role !== 'superadmin'}
          />
          <div className="modal-actions">
            <Button variant="secondary" type="button" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete User"
        size="small"
      >
        <p>Are you sure you want to delete {user?.firstName} {user?.lastName}?</p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default UserDetail

